import debounce from "lodash/debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ControlProps, GroupBase, GroupProps, OptionProps, StylesConfig } from "react-select";
import { components, mergeStyles } from "react-select";
import AsyncSelect from "react-select/async";

import useSystemStore from "@store/useSystemStore";
import { APP_COLORS } from "@styles/colors";
import { DealStageStatus } from "@enums/common.enum";
import { cn } from "@utils/tailwind.util";
import {
  IPlatformCompany,
  ISuggestionCompany,
  type ICompany,
  type ICompanyDeal,
} from "@interfaces/company.interface";
import { CompanyTypeSubtype } from "@enums/companies.enum";
import { useSearchCompanies } from "@components/AddNewEntity/hooks/useSearchCompanies";
import { useCompanySuggestions } from "@components/AddNewEntity/hooks/useCompanySuggestions";
import { CompanyIconBadge } from "@components/AddNewEntity/Shared/CompanyIconBadge";
import { useMaxMenuHeight } from "@components/AddNewEntity/hooks/useMaxMenuHeight";
import {
  ClearIndicator,
  DropdownIndicator,
  getCustomSelectStyles,
  type SelectProps,
} from "@ui/Select";
import { FieldErrorMessage } from "@ui/FormElements";

export interface ICompanySelectOption {
  value: string;
  label: string;
  website: string | null;
  logo_url?: string | null;
  suggestion?: ISuggestionCompany;
  isDisabled?: boolean | null;
  active_deal?: ICompanyDeal | null;
  type?: ICompany["type"] | null;
  priority?: ICompany["priority"] | null;
  detail_field_values?: ICompany["detail_field_values"] | null;
  is_manual?: boolean;
  platform_company?: IPlatformCompany | null;
  description?: string | null;
  enriched_detail_field_values?: string[] | null;
  enriched_fields?: string[] | null;
}

const getCustomStyles = (maxMenuHeight: number): StylesConfig<ICompanySelectOption, false> => {
  const { isDarkMode } = useSystemStore();

  return {
    clearIndicator: (provided) => ({
      ...provided,
      marginRight: "4px",
    }),
    control: (provided, { isFocused }) => ({
      ...provided,
      borderRadius: "8px",
      borderWidth: 1,
      borderColor: isFocused
        ? isDarkMode
          ? APP_COLORS.stroke.actionPrimaryDark
          : APP_COLORS.stroke.actionPrimary
        : isDarkMode
          ? APP_COLORS.stroke.primaryDark
          : APP_COLORS.stroke.primary,
      backgroundColor: isDarkMode
        ? APP_COLORS.background.primaryDark
        : APP_COLORS.background.primary,
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
      boxShadow: isFocused
        ? "0px 0px 0px 3px #B9D6D1" // theme.boxShadow.mainButtonFocus
        : "none",
      ":hover": {
        borderColor: isDarkMode ? APP_COLORS.stroke.secondaryDark : APP_COLORS.stroke.secondary,
      },
      ":focus": {
        borderColor: isDarkMode
          ? APP_COLORS.stroke.actionPrimaryDark
          : APP_COLORS.stroke.actionPrimary,
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
    }),
    menu: (provided) => ({
      ...provided,
      // Above the form's sticky footer (z-[1]) so the list can use the full panel height.
      zIndex: 2,
      maxHeight: maxMenuHeight,
      borderWidth: 1,
      borderRadius: 8,
      borderColor: isDarkMode ? APP_COLORS.stroke.primaryDark : APP_COLORS.stroke.primary,
      backgroundColor: isDarkMode
        ? APP_COLORS.background.primaryDark
        : APP_COLORS.background.primary,
      overflow: "hidden",
    }),
    option: (provided, { isSelected, isFocused }) => ({
      ...provided,
      backgroundColor: isSelected
        ? isDarkMode
          ? APP_COLORS.background.primaryDark
          : APP_COLORS.background.primary
        : isFocused
          ? isDarkMode
            ? APP_COLORS.background.primaryDark
            : APP_COLORS.background.primary
          : "transparent",
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
      ":hover": {
        backgroundColor: isDarkMode
          ? APP_COLORS.background.secondaryDark
          : APP_COLORS.background.secondary,
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: APP_COLORS.gray["400"],
    }),
    indicatorSeparator: () => ({ display: "none" }),
    input: (provided) => ({
      ...provided,
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: maxMenuHeight,
    }),
  };
};

const CustomOption = (
  props: OptionProps<ICompanySelectOption, false, GroupBase<ICompanySelectOption>>
) => {
  const { data } = props;

  return (
    <components.Option {...props} className="!p-0">
      <div
        className="flex items-center px-3 py-2 gap-2 cursor-pointer"
        style={{
          minHeight: 44,
        }}
      >
        <CompanyIconBadge
          url={data.logo_url}
          size="MEDIUM"
          className={cn("h-7 w-7 rounded-md border border-gray-200 bg-white flex-shrink-0", {
            "opacity-40": data.isDisabled,
          })}
        />
        <div className="flex flex-col min-w-0">
          <span
            className={cn("font-medium text-sm truncate", {
              "text-content-secondary": data.isDisabled,
            })}
          >
            {data.label}
          </span>
          {data.website && (
            <span
              className={cn("text-xs truncate", {
                "text-content-secondary": data.isDisabled,
              })}
            >
              {data.website.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "")}
            </span>
          )}
        </div>
      </div>
    </components.Option>
  );
};

const Control = ({
  children,
  ...props
}: ControlProps<ICompanySelectOption, false, GroupBase<ICompanySelectOption>>) => {
  const value = props.getValue();
  const showLogo = props.hasValue && value.length > 0;

  return (
    <components.Control {...props}>
      {showLogo && (
        <CompanyIconBadge
          url={value[0]?.logo_url}
          size="MEDIUM"
          className="ml-3 h-7 w-7 flex-shrink-0 overflow-hidden rounded-md"
        />
      )}
      {children}
    </components.Control>
  );
};

const Group = (props: GroupProps<ICompanySelectOption, false>) => {
  const { children, label } = props;
  return (
    <div>
      {label && (
        <div className="px-3 pt-2 pb-1">
          <span className="text-[10px] font-semibold uppercase text-gray-400 tracking-wider">
            {label}
          </span>
        </div>
      )}
      <div>{children}</div>
    </div>
  );
};

export function CompanySelect({
  notDisabledCompanies = [],
  invalid = false,
  errorMessage,
  ...rest
}: SelectProps<ICompanySelectOption> & {
  notDisabledCompanies?: string[];
  invalid?: boolean;
  errorMessage?: string;
}) {
  const callbackRef = useRef<any>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectInputValue, setSelectInputValue] = useState<string | undefined>(undefined);

  const maxMenuHeight = useMaxMenuHeight(dropdownButtonRef);

  const {
    data: searchedCompanies,
    isLoading: isLoadingSearchedCompanies,
    fetchStatus: fetchStatusSearchedCompanies,
  } = useSearchCompanies({ searchQuery, currentCompanies: [] });
  const {
    data: companySuggestions,
    isLoading: isLoadingCompanySuggestions,
    fetchStatus: fetchStatusCompanySuggestions,
  } = useCompanySuggestions(searchQuery);

  const options: GroupBase<ICompanySelectOption>[] = useMemo(() => {
    if (!searchedCompanies && !companySuggestions) return [];

    const parsedSearchedCompanies: ICompanySelectOption[] =
      searchedCompanies
        ?.filter(
          (company) =>
            company.type?.subtype === CompanyTypeSubtype.PLATFORM ||
            company.type?.subtype === CompanyTypeSubtype.PORTFOLIO
        )
        ?.map((company) => ({
          value: company.id,
          label: company.title,
          website: company.website || null,
          logo_url: company.logo_url,
          isDisabled:
            !notDisabledCompanies.includes(company.id) && company.active_deal
              ? company.active_deal.stage.status !== DealStageStatus.NOT_SHOWN_ON_PIPELINE
              : null,
          active_deal: company.active_deal,
          type: company.type || null,
          priority: company.priority || null,
          detail_field_values: company.detail_field_values || null,
          platform_company: company.platform_company || null,
          description: company.description || null,
          enriched_detail_field_values: company.enriched_detail_field_values || null,
          enriched_fields: company.enriched_fields || null,
        }))
        ?.sort((a, b) => {
          if (a.isDisabled === b.isDisabled) return 0;
          return a.isDisabled ? 1 : -1;
        }) || [];

    const parsedSuggestions: ICompanySelectOption[] =
      companySuggestions?.map((company) => ({
        value: company.website || "",
        label: company.name || "",
        website: company.website || null,
        logo_url: company.logo || null,
        suggestion: company,
      })) || [];

    return [
      {
        label: "COMPANIES IN MERIDIAN",
        options: parsedSearchedCompanies,
      },
      ...(parsedSuggestions.length > 0
        ? [
            {
              label: "SUGGESTIONS FROM GLOBAL DATABASE",
              options: parsedSuggestions,
            },
          ]
        : []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedCompanies, companySuggestions]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      setSelectInputValue("");
    }
  };

  useEffect(() => {
    if (
      isLoadingSearchedCompanies ||
      isLoadingCompanySuggestions ||
      fetchStatusSearchedCompanies !== "idle" ||
      fetchStatusCompanySuggestions !== "idle"
    )
      return;
    if (callbackRef.current) {
      if (searchQuery && options.length > 0) {
        callbackRef.current(options);
      } else {
        callbackRef.current([]);
      }
    }
  }, [
    isLoadingCompanySuggestions,
    isLoadingSearchedCompanies,
    options,
    searchQuery,
    fetchStatusSearchedCompanies,
    fetchStatusCompanySuggestions,
  ]);

  const noOptionsMessage = ({ inputValue }: { inputValue: string }) =>
    inputValue.length > 0 ? (
      <span className="text-sm">No companies found with this name.</span>
    ) : (
      "No companies"
    );

  const debouncedSearch = useMemo(() => {
    return debounce((inputValue: string) => {
      setSearchQuery(inputValue);
    }, 300);
  }, []);

  const loadOptions = (_: string, callback: (arr: Array<ICompanySelectOption>) => void) => {
    callbackRef.current = callback;
  };

  return (
    <div ref={dropdownButtonRef}>
      <AsyncSelect<ICompanySelectOption, false>
        {...rest}
        onChange={(newValue, actionMeta) => {
          setSelectInputValue(undefined);
          rest.onChange?.(newValue, actionMeta);
        }}
        components={{ Option: CustomOption, Control, DropdownIndicator, ClearIndicator, Group }}
        isMulti={false}
        styles={mergeStyles(getCustomSelectStyles(), getCustomStyles(maxMenuHeight))}
        cacheOptions
        options={options}
        menuPosition="absolute"
        noOptionsMessage={noOptionsMessage}
        isClearable={true}
        defaultOptions={options}
        placeholder="Select a company"
        onKeyDown={handleKeyDown}
        onInputChange={(inputValue, actionMeta) => {
          if (actionMeta.action === "input-change") {
            setSelectInputValue(!inputValue ? undefined : inputValue);
            debouncedSearch(inputValue);
            rest.onInputChange?.(inputValue, actionMeta);
          }
        }}
        inputValue={selectInputValue}
        loadOptions={loadOptions}
        isLoading={isLoadingSearchedCompanies || isLoadingCompanySuggestions}
        aria-invalid={true}
      />
      <FieldErrorMessage message={errorMessage} />
    </div>
  );
}
